/**
 * Task-note data layer — TanStack Query hooks over the gated per-node notes
 * API (`/api/teams/{teamId}/tasks/nodes/{nodeId}/notes`). This is the ONLY
 * place the notes layer talks to the server; the rendering component (the
 * drawer's Notes tab) consumes these hooks and never calls `apiFetch`
 * directly — the same query ⟂ view split the rest of taskmap keeps (§A7).
 *
 * Issue #767 (Phase 4). A note is lightweight per-node feedback / provenance
 * (§A3) — context attached to the work, not a chat channel, and **never a
 * scorecard about a person** (§A9). The list is keyed
 * `["task-node-notes", teamId, nodeId]`; both mutations invalidate that key on
 * success so the thread re-derives from one fresh source of truth.
 *
 * Gating: these hooks are only *consumed* behind `task_notes` (off by
 * default, §A10); the backend enforces the gate too. The list query is
 * `enabled` only while the Notes tab is actually open (both ids present), so
 * opening a drawer never fires the request until needed. It degrades calmly on
 * a 404 (`retry: false`, empty list) so an undeployed/ungated endpoint never
 * crashes the drawer.
 *
 * Isolation: imports only the generic `apiFetch` + the generated schema
 * types — no scheduler coupling, no rendering.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";

/** One note, as it arrives from the API. */
export type TaskNote = components["schemas"]["TaskNoteRead"];
type NoteListResponse = components["schemas"]["NoteListResponse"];
type NoteResponse = components["schemas"]["NoteResponse"];

/** Cache key for one node's note thread. Exported for tests + invalidation. */
export const TASK_NODE_NOTES_KEY = (teamId: string, nodeId: string) =>
  ["task-node-notes", teamId, nodeId] as const;

const STALE_TIME_MS = 30_000;

function notesUrl(teamId: string, nodeId: string): string {
  return `/api/teams/${teamId}/tasks/nodes/${nodeId}/notes`;
}

export interface UseNodeNotesResult {
  /** The thread, as the endpoint returns it (newest-first, §B4.1). */
  readonly notes: TaskNote[];
  readonly isLoading: boolean;
  /** True on any fetch failure (incl. a not-yet-deployed / ungated 404). */
  readonly isError: boolean;
}

/**
 * Fetch one node's note thread. `enabled` only when both ids are present
 * (i.e. the Notes tab is actually open) so we never fire a request for a
 * closed drawer or an inactive tab. Degrades to an empty list on a
 * non-array / errored body so the view never has to null-check.
 */
export function useNodeNotes(
  teamId: string | undefined,
  nodeId: string | undefined,
): UseNodeNotesResult {
  const query = useQuery({
    queryKey: TASK_NODE_NOTES_KEY(teamId ?? "", nodeId ?? ""),
    enabled: Boolean(teamId) && Boolean(nodeId),
    staleTime: STALE_TIME_MS,
    // No retry: a not-yet-deployed / ungated endpoint should fail fast to the
    // calm "Notes unavailable" state rather than spinning.
    retry: false,
    queryFn: () => apiFetch<NoteListResponse>(notesUrl(teamId as string, nodeId as string)),
  });

  const notes = Array.isArray(query.data?.notes) ? query.data.notes : [];
  return { notes, isLoading: query.isLoading, isError: query.isError };
}

interface AddNoteArgs {
  /** The note text (required — an empty body is a 422 server-side). */
  body: string;
  /**
   * Membership ids this note `@`-references (drives the #765 digest's "notes
   * that mention you" seam, §A4.1) — a list of WHO is mentioned, never a count
   * about a person (§A9). Omit / empty when nobody is mentioned.
   */
  mentions?: string[];
}

/**
 * Post a note on the node (POST → 201). Invalidates the thread so it re-renders
 * with the new note. An empty body is rejected (422) — the compose box guards
 * against that up front (the post button is disabled), so this is belt-and-
 * braces.
 */
export function useAddNodeNote(teamId: string | undefined, nodeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, mentions }: AddNoteArgs) =>
      apiFetch<NoteResponse>(notesUrl(teamId as string, nodeId as string), {
        method: "POST",
        body: JSON.stringify({ body, ...(mentions && mentions.length > 0 ? { mentions } : {}) }),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: TASK_NODE_NOTES_KEY(teamId ?? "", nodeId ?? "") }),
  });
}

/**
 * Remove a note by its id (DELETE → 204). Invalidates the thread so the note
 * drops on success. The backend permits this only for the note's author or a
 * lead / co_lead (403 otherwise); the UI only shows the delete affordance to
 * those actors, so a 403 should not normally be reachable.
 */
export function useRemoveNodeNote(teamId: string | undefined, nodeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) =>
      apiFetch<void>(`${notesUrl(teamId as string, nodeId as string)}/${noteId}`, {
        method: "DELETE",
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: TASK_NODE_NOTES_KEY(teamId ?? "", nodeId ?? "") }),
  });
}
